import {
  Create,
  Delete,
  DeleteForever,
  HelpOutline,
  Undo,
} from "@mui/icons-material";
import { ButtonGroup, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "common/locales";
import type { Feature, Polygon } from "geojson";
import * as mapboxgl from "mapbox-gl";
// All map editor actions migrated to useUIStore
import { useCallback, useEffect, useRef, useState } from "react";
import { useMap } from "react-map-gl/mapbox";
import { useUIStore } from "stores";
import {
  GeoJSONStoreFeatures,
  TerraDraw,
  TerraDrawPolygonMode,
  TerraDrawRenderMode,
  TerraDrawSelectMode,
} from "terra-draw";
import { TerraDrawMapboxGLAdapter } from "terra-draw-mapbox-gl-adapter";
import { DrawControlButton } from "./DrawControlButton";
import DrawingHelpModal from "./DrawingHelpModal";
import { EditorSelectionType } from "./enums";
import FieldBoundaryDeleteConfirmationDialog from "./FieldBoundaryDeleteConfirmationDialog";

const POLYGON_COLOR = "#2681F2";
const POLYGON_SELECTED_COLOR = "#2681F2";

export default function TerraDrawMapEditor() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Get actions from Zustand
  const setEditorIsInTentativeMode = useUIStore(
    (state) => state.setEditorIsInTentativeMode
  );
  const setEditorSelectionType = useUIStore(
    (state) => state.setEditorSelectionType
  );
  const setTentativeFeature = useUIStore((state) => state.setTentativeFeature);
  const setTmpFeatures = useUIStore((state) => state.setTmpFeatures);
  const deleteTmpFeature = useUIStore((state) => state.deleteTmpFeature);

  const mapContext = useMap();
  const map = mapContext.current;

  const terraDrawRef = useRef<TerraDraw | null>(null);
  const [isReady, setIsReady] = useState(false);
  const featuresLoadedRef = useRef(false);

  // Force re-render when map becomes available (refs don't trigger re-renders)
  const [, forceUpdate] = useState(0);

  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [fieldBoundaryDeleteDialogOpen, setFieldBoundaryDeleteDialogOpen] =
    useState(false);

  const selectionType = useUIStore((state) => state.editorSelectionType);
  const editorIsInTentativeMode = useUIStore(
    (state) => state.editorIsInTentativeMode
  );
  const features = useUIStore((state) => state.tmpFeatures);

  // Ref to track tentative mode for use in event listeners (avoids stale closures)
  const editorIsInTentativeModeRef = useRef(editorIsInTentativeMode);
  useEffect(() => {
    editorIsInTentativeModeRef.current = editorIsInTentativeMode;
  }, [editorIsInTentativeMode]);

  const hasFeature = features && features.length > 0;
  const isEditing = selectionType !== EditorSelectionType.NONE;

  // Force a re-render when map becomes available (refs don't trigger re-renders)
  useEffect(() => {
    if (!map) {
      // Poll for map availability
      const checkMap = setInterval(() => {
        if (mapContext.current) {
          forceUpdate((n) => n + 1);
          clearInterval(checkMap);
        }
      }, 100);
      return () => clearInterval(checkMap);
    }
  }, [map, mapContext]);

  // Initialize Terra Draw when map is ready
  useEffect(() => {
    // Skip if already initialized - terraDrawRef.current is the source of truth
    if (terraDrawRef.current) return;
    if (!map) return;

    const mapInstance = map.getMap();
    if (!mapInstance) return;

    const initTerraDraw = () => {
      // Double-check we haven't already initialized (race condition protection)
      if (terraDrawRef.current) return;
      const terraDraw = new TerraDraw({
        adapter: new TerraDrawMapboxGLAdapter({
          map: mapInstance,
          lib: mapboxgl,
        }),
        modes: [
          new TerraDrawPolygonMode({
            styles: {
              fillColor: POLYGON_COLOR,
              fillOpacity: 0.3,
              outlineColor: POLYGON_COLOR,
              outlineWidth: 2,
              closingPointColor: POLYGON_COLOR,
              closingPointWidth: 8,
              closingPointOutlineColor: "#ffffff",
              closingPointOutlineWidth: 2,
            },
          }),
          new TerraDrawSelectMode({
            flags: {
              polygon: {
                feature: {
                  draggable: false,
                  coordinates: {
                    deletable: true,
                    draggable: true,
                  },
                },
              },
            },
            styles: {
              selectedPolygonColor: POLYGON_SELECTED_COLOR,
              selectedPolygonFillOpacity: 0.4,
              selectedPolygonOutlineColor: POLYGON_SELECTED_COLOR,
              selectedPolygonOutlineWidth: 4,
              selectionPointColor: POLYGON_SELECTED_COLOR,
              selectionPointWidth: 10,
              selectionPointOutlineColor: "#ffffff",
              selectionPointOutlineWidth: 2,
              midPointColor: "#ffffff",
              midPointWidth: 6,
              midPointOutlineColor: POLYGON_SELECTED_COLOR,
              midPointOutlineWidth: 2,
            },
          }),
          new TerraDrawRenderMode({
            modeName: "render",
            styles: {
              polygonFillColor: POLYGON_COLOR,
              polygonFillOpacity: 0.2,
              polygonOutlineColor: POLYGON_COLOR,
              polygonOutlineWidth: 2,
            },
          }),
        ],
      });

      terraDraw.start();
      terraDrawRef.current = terraDraw;

      // Listen for finish events (polygon completed)
      terraDraw.on("finish", (id) => {
        const snapshot = terraDraw.getSnapshot();
        const feature = snapshot.find((f) => f.id === id);
        if (feature && feature.geometry.type === "Polygon") {
          const geoJsonFeature: Feature<Polygon> = {
            type: "Feature",
            properties: { guid: "new", id: String(id) },
            geometry: feature.geometry,
          };
          setTmpFeatures([geoJsonFeature] as Feature[]);
          setTentativeFeature(null);
          setEditorIsInTentativeMode(false);
          setEditorSelectionType(EditorSelectionType.NONE);
          terraDraw.setMode("select");
        }
      });

      // Listen for change events - sync polygon features to React state
      // NOTE: We only sync when NOT in tentative mode (drawing), to avoid
      // React state updates interfering with Terra Draw's internal state.
      // During drawing, Terra Draw manages its own state. We sync on finish.
      terraDraw.on("change", (ids, type) => {
        // Skip state updates during drawing to avoid interfering with Terra Draw
        if (editorIsInTentativeModeRef.current) {
          return;
        }
        if (type === "create" || type === "update" || type === "delete") {
          const snapshot = terraDraw.getSnapshot();
          const polygons = snapshot
            .filter((f) => f.geometry.type === "Polygon")
            .map((f) => ({
              type: "Feature" as const,
              properties: {
                guid: f.properties?.guid || "new",
                id: String(f.id),
              },
              geometry: f.geometry as Polygon,
            }));
          if (polygons.length > 0) {
            setTmpFeatures(polygons);
          } else {
            setTmpFeatures([]);
          }
        }
      });

      // Listen for select events - update React state to reflect selection
      // We only update when NOT in tentative mode (drawing), since during drawing
      // Terra Draw may fire select events for internal coordinate management
      terraDraw.on("select", () => {
        if (!editorIsInTentativeModeRef.current) {
          setEditorSelectionType(EditorSelectionType.FEATURE);
        }
      });

      // Listen for deselect events
      terraDraw.on("deselect", () => {
        if (!editorIsInTentativeModeRef.current) {
          setEditorSelectionType(EditorSelectionType.NONE);
        }
      });

      setIsReady(true);
    };

    // Wait for map style to be loaded before initializing Terra Draw
    // Note: With reuseMaps, the 'load' event may not fire if map was already loaded
    // So we also poll for style availability as a fallback
    if (mapInstance.isStyleLoaded()) {
      initTerraDraw();
    } else {
      // Try the 'load' event first
      mapInstance.on("load", initTerraDraw);

      // Also poll as fallback since 'load' might not fire with reuseMaps
      const pollInterval = setInterval(() => {
        if (terraDrawRef.current) {
          clearInterval(pollInterval);
          return;
        }
        if (mapInstance.isStyleLoaded()) {
          clearInterval(pollInterval);
          initTerraDraw();
        }
      }, 100);

      // Store interval for cleanup
      (
        mapInstance as Record<string, NodeJS.Timeout | number | undefined>
      ).__terraDrawPollInterval = pollInterval;
    }

    return () => {
      mapInstance.off("load", initTerraDraw);
      // Clean up poll interval if it exists
      const pollInterval = (
        mapInstance as Record<string, NodeJS.Timeout | number | undefined>
      ).__terraDrawPollInterval;
      if (pollInterval) {
        clearInterval(pollInterval);
        delete (
          mapInstance as Record<string, NodeJS.Timeout | number | undefined>
        ).__terraDrawPollInterval;
      }
      if (terraDrawRef.current) {
        terraDrawRef.current.stop();
        terraDrawRef.current = null;
      }
      setIsReady(false);
    };
  }, [
    map,
    setTmpFeatures,
    setTentativeFeature,
    setEditorIsInTentativeMode,
    setEditorSelectionType,
  ]);

  // Track the Terra Draw feature ID for the loaded polygon
  const loadedFeatureIdRef = useRef<string | number | undefined>(undefined);

  // Load existing features when component mounts or features change
  useEffect(() => {
    const terraDraw = terraDrawRef.current;
    if (!terraDraw || !isReady) return;

    // Only load features once when they first become available
    // This prevents clearing user edits when state updates
    if (featuresLoadedRef.current) return;

    // Add existing features (e.g., when editing an existing field)
    if (features && features.length > 0) {
      featuresLoadedRef.current = true;

      // Clear any existing features in Terra Draw before loading
      terraDraw.clear();

      features.forEach((feature) => {
        if (feature.geometry && feature.geometry.type === "Polygon") {
          // Round coordinates to 6 decimal places (Terra Draw rejects excessive precision)
          const roundedCoordinates = (
            feature.geometry.coordinates as number[][][]
          ).map((ring) =>
            ring.map((coord) => [
              Math.round(coord[0] * 1e6) / 1e6,
              Math.round(coord[1] * 1e6) / 1e6,
            ])
          );

          // Add feature with mode "polygon" so it can be edited
          const results = terraDraw.addFeatures([
            {
              type: "Feature",
              properties: { mode: "polygon", ...feature.properties },
              geometry: {
                type: "Polygon",
                coordinates: roundedCoordinates,
              },
            } as GeoJSONStoreFeatures,
          ]);
          if (results && results.length > 0 && results[0].valid) {
            loadedFeatureIdRef.current = results[0].id;
          }
        }
      });

      // Switch to select mode and select the feature so it's visible
      if (loadedFeatureIdRef.current !== undefined) {
        terraDraw.setMode("select");
        terraDraw.selectFeature(loadedFeatureIdRef.current);
        setEditorSelectionType(EditorSelectionType.FEATURE);
      }
    }
  }, [isReady, features, setEditorSelectionType]);

  // NOTE: We intentionally do NOT have a useEffect that syncs Terra Draw mode with React state.
  // Terra Draw manages its own internal mode state, and trying to sync it via useEffect
  // causes issues because React state updates are asynchronous and can interfere with
  // Terra Draw's internal state machine (especially during drawing operations).
  //
  // Mode changes should ONLY happen in response to explicit user actions (button clicks),
  // not in reaction to state changes. This follows the pattern used by maplibre-gl-terradraw.

  const handleFieldDrawClick = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }

      const terraDraw = terraDrawRef.current;

      if (editorIsInTentativeMode) {
        // Cancel drawing mode
        if (terraDraw) {
          terraDraw.setMode("render");
        }
        setEditorIsInTentativeMode(false);
        setEditorSelectionType(EditorSelectionType.NONE);
        setTentativeFeature(null);
        return;
      }

      if (!hasFeature) {
        // Start drawing mode
        if (terraDraw) {
          terraDraw.setMode("polygon");
        }
        setEditorIsInTentativeMode(true);
        setEditorSelectionType(EditorSelectionType.TENTATIVE_POSITION);
        return;
      }

      // Toggle selection - when editing existing feature, clicking pencil toggles selection
      if (selectionType === EditorSelectionType.FEATURE) {
        // Deselect (but feature remains visible because we stay in select mode)
        setEditorSelectionType(EditorSelectionType.NONE);
      } else {
        // Select the feature for editing
        setEditorSelectionType(EditorSelectionType.FEATURE);
        if (terraDraw) {
          terraDraw.setMode("select");
          // Select the polygon feature
          const featureId = loadedFeatureIdRef.current;
          if (featureId !== undefined) {
            terraDraw.selectFeature(featureId);
          } else {
            // No stored ID, try to find the polygon feature
            const snapshot = terraDraw.getSnapshot();
            const polygonFeature = snapshot.find(
              (f) => f.geometry.type === "Polygon"
            );
            if (polygonFeature) {
              terraDraw.selectFeature(polygonFeature.id);
              loadedFeatureIdRef.current = polygonFeature.id;
            }
          }
        }
      }
    },
    [
      editorIsInTentativeMode,
      hasFeature,
      selectionType,
      setEditorIsInTentativeMode,
      setEditorSelectionType,
      setTentativeFeature,
    ]
  );

  const handleDelete = useCallback(() => {
    const terraDraw = terraDrawRef.current;

    if (editorIsInTentativeMode) {
      // Cancel current drawing
      if (terraDraw) {
        terraDraw.setMode("render");
        terraDraw.setMode("polygon"); // Reset polygon mode to clear tentative points
      }
      return;
    }

    if (hasFeature && selectionType === EditorSelectionType.NONE) {
      // Confirm delete entire feature
      setFieldBoundaryDeleteDialogOpen(true);
      return;
    }

    // Delete selected feature
    if (terraDraw && selectionType === EditorSelectionType.FEATURE) {
      const snapshot = terraDraw.getSnapshot();
      const polygonIds = snapshot
        .filter((f) => f.geometry.type === "Polygon")
        .map((f) => f.id);
      if (polygonIds.length > 0) {
        setFieldBoundaryDeleteDialogOpen(true);
      }
    }
  }, [editorIsInTentativeMode, hasFeature, selectionType]);

  const handleFieldBoundaryDeleteAction = useCallback(
    (deleteConfirmed: boolean) => {
      if (deleteConfirmed) {
        const terraDraw = terraDrawRef.current;
        if (terraDraw) {
          const snapshot = terraDraw.getSnapshot();
          const polygonIds = snapshot
            .filter((f) => f.geometry.type === "Polygon")
            .map((f) => f.id);
          terraDraw.removeFeatures(polygonIds);
        }
        // Reset feature refs since we deleted them
        loadedFeatureIdRef.current = undefined;
        featuresLoadedRef.current = false;

        setTmpFeatures([]);
        if (features && features[0]?.properties?.id) {
          deleteTmpFeature(String(features[0].properties.id));
        }
        setEditorIsInTentativeMode(false);
        setEditorSelectionType(EditorSelectionType.NONE);
      }
      setFieldBoundaryDeleteDialogOpen(false);
    },
    [
      features,
      setTmpFeatures,
      deleteTmpFeature,
      setEditorIsInTentativeMode,
      setEditorSelectionType,
    ]
  );

  const handleHelpClick = useCallback(() => {
    setHelpModalOpen(true);
  }, []);

  const handleHelpModalClose = useCallback(() => {
    setHelpModalOpen(false);
  }, []);

  // Determine delete button state
  let canDelete = false;
  let deleteIcon = <DeleteForever />;
  let deleteTooltip = "map.deletefeature.tooltip";

  if (editorIsInTentativeMode) {
    deleteIcon = <Undo />;
    deleteTooltip = "map.deleteTentativePosition.tooltip";
    canDelete = true;
  } else if (hasFeature) {
    canDelete = true;
    if (selectionType === EditorSelectionType.FEATURE) {
      deleteIcon = <Delete />;
      deleteTooltip = "map.deleteSelectedHandles.tooltip";
    }
  }

  // Check if panel is showing (for positioning)
  const isPanelShowing = !isMobile;

  return (
    <>
      <DrawControl $isPanelShowing={isPanelShowing}>
        <ButtonGroup
          orientation="vertical"
          variant="contained"
          aria-label={t("map.drawfield.drawTools.aria")}
          size="medium"
          disableElevation
        >
          <Tooltip title={t("map.drawfield.tooltip")} placement="right">
            <DrawControlButton
              $pressed={isEditing || editorIsInTentativeMode}
              variant="contained"
              onClick={handleFieldDrawClick}
              size="medium"
            >
              <Create />
            </DrawControlButton>
          </Tooltip>
          <Tooltip title={t(deleteTooltip)} placement="right">
            <DrawControlButton
              onClick={canDelete ? handleDelete : undefined}
              {...{ component: "div" }}
              disabled={!canDelete}
              variant="contained"
              size="medium"
            >
              {deleteIcon}
            </DrawControlButton>
          </Tooltip>
          <Tooltip title={t("map.drawfield.help.tooltip")} placement="right">
            <DrawControlButton
              onClick={handleHelpClick}
              variant="contained"
              size="medium"
            >
              <HelpOutline />
            </DrawControlButton>
          </Tooltip>
        </ButtonGroup>
      </DrawControl>
      <FieldBoundaryDeleteConfirmationDialog
        open={fieldBoundaryDeleteDialogOpen}
        handleAction={handleFieldBoundaryDeleteAction}
      />
      <DrawingHelpModal
        isOpen={helpModalOpen}
        handleClose={handleHelpModalClose}
      />
    </>
  );
}

const DrawControl = styled("div", {
  shouldForwardProp: (prop) => prop !== "$isPanelShowing",
})<{ $isPanelShowing?: boolean }>(({ theme, $isPanelShowing }) => ({
  backgroundColor: "white",
  borderRadius: theme.spacing(1),
  position: "fixed",
  top: theme.spacing(2),
  left: $isPanelShowing ? "616px" : "72px",
  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
  zIndex: 10,
  [theme.breakpoints.down("sm")]: {
    left: theme.spacing(1),
    top: `calc(${theme.spacing(14)} + env(safe-area-inset-top))`,
  },
}));
